import React, { useEffect, useState } from 'react';
import { Space, Button, Popconfirm, Tag, Popover, message } from 'antd';
import { EditOutlined, EyeOutlined, CopyOutlined, DeleteOutlined, ScheduleOutlined, PlusCircleOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Order, FilterOption } from '../services/order';
import { SERVICE_TYPE_MAP } from './service-type-map';
import mediaService from '../services/media';
import { TRADEMARK_OFFICES } from '../utils/trademarkUrls';

// 简易媒体URL缓存，避免列表重复请求
const mediaUrlCache = new Map<number, string>();

// 订单列表中的“加入监控”按钮需要和商标监控实际支持的国家保持同一份来源，
// 避免前端继续手写国家名单，导致新接入爬虫后这里漏显示按钮。
const monitoredNationValues = new Set(
  TRADEMARK_OFFICES.map((office) => office.nation)
);

// 投资订单出售状态允许出现多个业务值，列表颜色需要和表单选项保持一致。
const saleStatusTagColorMap: Record<string, string> = {
  '在售': 'blue',
  '已出售': 'green',
  '不可售': 'default',
};

const TrademarkImageCell: React.FC<{ mediaId?: number }> = ({ mediaId }) => {
  const [url, setUrl] = useState<string>('');

  useEffect(() => {
    const id = mediaId ? Number(mediaId) : NaN;
    if (!id || Number.isNaN(id)) {
      setUrl('');
      return;
    }
    if (mediaUrlCache.has(id)) {
      setUrl(mediaUrlCache.get(id) || '');
      return;
    }
    mediaService.getMedia(id).then((res) => {
      if (res.success && res.data) {
        const resolved = mediaService.getMediaUrl(res.data);
        mediaUrlCache.set(id, resolved);
        setUrl(resolved);
      } else {
        mediaUrlCache.set(id, '');
        setUrl('');
      }
    }).catch(() => {
      mediaUrlCache.set(id, '');
      setUrl('');
    });
  }, [mediaId]);

  if (!mediaId || !url) {
    return (
      <div
        style={{
          width: 64,
          height: 48,
          border: '1px solid #f0f0f0',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: 12,
          background: '#fafafa',
        }}
      >
        无图片
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img
        src={url}
        alt="商标图样"
        loading="lazy"
        style={{ width: 64, height: 48, objectFit: 'contain', borderRadius: 4, border: '1px solid #f0f0f0' }}
      />
    </a>
  );
};

// 文件列表单元格组件
// 只使用后端预加载的 files_info 数据，不发送前端请求
interface FileInfo {
  id: number;
  name: string;
  url: string;
}

const FilesCell: React.FC<{ filesInfo: FileInfo[] }> = ({ filesInfo }) => {
  if (!filesInfo || filesInfo.length === 0) {
    return <span style={{ color: '#999', fontStyle: 'italic' }}>无文件</span>;
  }

  return (
    <div style={{ maxWidth: '280px' }}>
      {filesInfo.map((file, index) => (
        <div key={file.id} style={{
          marginBottom: index < filesInfo.length - 1 ? 4 : 0,
        }}>
          <span
            onClick={() => mediaService.downloadMedia(file.id, file.name)}
            style={{
              padding: 0,
              fontSize: '12px',
              color: '#1890ff',
              textAlign: 'left',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '260px',
              cursor: 'pointer'
            }}
            title={file.name}
          >
            • {file.name}
          </span>
        </div>
      ))}
    </div>
  );
};


// 入参 deps 包含：serviceType、SERVICE_TYPES、handleEdit、handleDelete、handleCopy、handleViewDetail、handleAddToMonitor
// 以及渲染所需的 filterOptions 和 statusOptions
export interface BuildDeps {
  serviceType: string;
  SERVICE_TYPES: Array<{ value: string; label: string; icon?: React.ReactNode }>;
  handleEdit: (order: Order) => void;
  handleDelete: (id: number) => void;
  handleCopy: (order: Order) => void;
  handleViewDetail: (orderId?: number) => void;
  handleAddToMonitor: (order: Order, applicationNo?: string) => void;
  openUpdateStatusModal: (order: Order) => void;
  canEdit: boolean;
  canDelete: boolean;
  canCopy: boolean;
  canViewDetail: boolean;
  canUpdateStatus: boolean;
  canMonitor: boolean;
  filterOptions: {
    nations: FilterOption[];
    services: FilterOption[];
    payment_statuses: FilterOption[];
    applicant_names: FilterOption[];
    channels: FilterOption[];
    sources: FilterOption[];
    statuses: FilterOption[];
  };
  statusOptions: { value: string; label: string; color?: string }[];
}

export function buildAllColumnDefinitions(deps: BuildDeps) {
  const {
    serviceType,
    SERVICE_TYPES,
    handleEdit,
    handleDelete,
    handleCopy,
    handleViewDetail,
    handleAddToMonitor,
    openUpdateStatusModal,
    canEdit,
    canDelete,
    canCopy,
    canViewDetail,
    canUpdateStatus,
    canMonitor,
    filterOptions,
    statusOptions,
  } = deps;

  const allColumnDefinitions: Record<string, any> = {

    status: {
      title: '最新状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (_: any, record: Order) => {
        const list = Array.isArray(record.statuses) ? record.statuses.slice() : [];
        if (list.length > 0) {
          list.sort((a: any, b: any) => dayjs(b.created_at || 0).unix() - dayjs(a.created_at || 0).unix());
          const s = list[0]?.status || '-';
          return s ? s : '-';
        }
        const fallback = record.status || '-';
        return fallback ? fallback : '-';
      },
    },
    id: {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left',
    },
    case_no: {
      title: '客户案件编号',
      dataIndex: 'case_no',
      key: 'case_no',
      width: 160,
      render: (value: string, record: Order) => value || record.case_no || '-',
    },
    group_number: {
      title: '客户群号',
      dataIndex: 'group_number',
      key: 'group_number',
      width: 120,
      render: (_: string, record: Order) => record.client?.group_number || record.group_number || '-',
    },
    commission: {
      title: '提成',
      dataIndex: 'commission',
      key: 'commission',
      width: 120,
      render: (value: any, record: Order) => record.commission ?? value ?? '-',
    },
    remark: {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
      ellipsis: true,
      render: (value: string, record: Order) => value || record.remark || '-',
    },
    files: {
      title: '文件',
      dataIndex: 'files',
      key: 'files',
      width: 300,
      render: (_: any, record: Order) => {
        // 使用后端预加载的 files_info 数据
        const filesInfo = (record as any).files_info || [];
        return <FilesCell filesInfo={filesInfo} />;
      },
    },
    updated_at: {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (value: string, record: Order) => {
        const ts = value || record.updated_at;
        return ts ? dayjs(ts).format('YYYY-MM-DD HH:mm') : '-';
      },
    },

    // ===== 版权扩展字段（record.copyright） =====
    work_name: {
      title: '作品名',
      dataIndex: 'work_name',
      key: 'work_name',
      width: 160,
      render: (_: any, record: Order) => (record.copyright as any)?.work_name || '-',
    },
    application_type_copyright: {
      title: '申请方式',
      dataIndex: 'application_type',
      key: 'application_type_copyright',
      width: 120,
      render: (_: any, record: Order) => (record.copyright as any)?.application_type || '-',
    },
    application_category: {
      title: '作品类型',
      dataIndex: 'application_category',
      key: 'application_category',
      width: 140,
      render: (_: any, record: Order) => (record.copyright as any)?.application_category || '-',
    },
    work_inspiration: {
      title: '作品来源及灵感',
      dataIndex: 'work_inspiration',
      key: 'work_inspiration',
      width: 200,
      render: (_: any, record: Order) => (record.copyright as any)?.work_inspiration || '-',
    },
    holder_name: {
      title: '持有人名字',
      dataIndex: 'holder_name',
      key: 'holder_name',
      width: 160,
      render: (_: any, record: Order) => (record.copyright as any)?.holder_name || '-',
    },
    holder_name_en: {
      title: '持有人英文名字',
      dataIndex: 'holder_name_en',
      key: 'holder_name_en',
      width: 180,
      render: (_: any, record: Order) => (record.copyright as any)?.holder_name_en || '-',
    },
    holder_address: {
      title: '持有人地址',
      dataIndex: 'holder_address',
      key: 'holder_address',
      width: 200,
      render: (_: any, record: Order) => (record.copyright as any)?.holder_address || '-',
    },
    holder_address_en: {
      title: '持有人地址英文',
      dataIndex: 'holder_address_en',
      key: 'holder_address_en',
      width: 220,
      render: (_: any, record: Order) => (record.copyright as any)?.holder_address_en || '-',
    },
    assignee_name: {
      title: '受让人名字',
      dataIndex: 'assignee_name',
      key: 'assignee_name',
      width: 160,
      render: (_: any, record: Order) => (record.copyright as any)?.assignee_name || (record.trademark as any)?.assignee_name || '-',
    },
    assignee_name_en: {
      title: '受让人英文名字',
      dataIndex: 'assignee_name_en',
      key: 'assignee_name_en',
      width: 180,
      render: (_: any, record: Order) => (record.copyright as any)?.assignee_name_en || (record.trademark as any)?.assignee_name_en || '-',
    },
    assignee_address: {
      title: '受让人地址',
      dataIndex: 'assignee_address',
      key: 'assignee_address',
      width: 200,
      render: (_: any, record: Order) => (record.copyright as any)?.assignee_address || (record.trademark as any)?.assignee_address || '-',
    },
    assignee_address_en: {
      title: '受让人地址英文',
      dataIndex: 'assignee_address_en',
      key: 'assignee_address_en',
      width: 220,
      render: (_: any, record: Order) => (record.copyright as any)?.assignee_address_en || (record.trademark as any)?.assignee_address_en || '-',
    },

    // ===== 专利扩展字段（record.patent） =====
    patent_name: {
      title: '专利名称',
      dataIndex: 'patent_name',
      key: 'patent_name',
      width: 160,
      render: (_: any, record: Order) => (record.patent as any)?.patent_name || '-',
    },
    include_drawing: {
      title: '包含制图',
      dataIndex: 'include_drawing',
      key: 'include_drawing',
      width: 100,
      render: (_: any, record: Order) => ((record.patent as any)?.include_drawing ? '是' : '否'),
    },
    accelerated: {
      title: '加速申请',
      dataIndex: 'accelerated',
      key: 'accelerated',
      width: 100,
      render: (_: any, record: Order) => ((record.patent as any)?.accelerated ? '是' : '否'),
    },
    include_authorization: {
      title: '包含授权',
      dataIndex: 'include_authorization',
      key: 'include_authorization',
      width: 100,
      render: (_: any, record: Order) => ((record.patent as any)?.include_authorization ? '是' : '否'),
    },
    include_translation: {
      title: '专利翻译',
      dataIndex: 'include_translation',
      key: 'include_translation',
      width: 100,
      render: (_: any, record: Order) => ((record.patent as any)?.include_translation ? '是' : '否'),
    },
    open_status: {
      title: '公开状态',
      dataIndex: 'open_status',
      key: 'open_status',
      width: 100,
      render: (_: any, record: Order) => (record.patent as any)?.open_status || (record.copyright as any)?.open_status || '-',
    },
    opened_at: {
      title: '公开日期',
      dataIndex: 'opened_at',
      key: 'opened_at',
      width: 120,
      render: (_: any, record: Order) => {
        const v = (record.patent as any)?.opened_at || (record.copyright as any)?.opened_at;
        return v ? dayjs(v).format('YYYY-MM-DD') : '-';
      },
    },
    opened_area: {
      title: '公开国家和城市',
      dataIndex: 'opened_area',
      key: 'opened_area',
      width: 160,
      render: (_: any, record: Order) => (record.patent as any)?.opened_area || (record.copyright as any)?.opened_area || '-',
    },
    opened_type: {
      title: '公开方式',
      dataIndex: 'opened_type',
      key: 'opened_type',
      width: 140,
      render: (_: any, record: Order) => {
        const v = (record.patent as any)?.opened_type || (record.copyright as any)?.opened_type;
        if (Array.isArray(v)) return v.join('，');
        return v || '-';
      },
    },
    claims_count: {
      title: '权利要求数量',
      dataIndex: 'claims_count',
      key: 'claims_count',
      width: 120,
      render: (_: any, record: Order) => (record.patent as any)?.claims_count || '-',
    },
    exceeding_quantity: {
      title: '超出数量',
      dataIndex: 'exceeding_quantity',
      key: 'exceeding_quantity',
      width: 100,
      render: (_: any, record: Order) => (record.patent as any)?.exceeding_quantity ?? '-',
    },
    include_document_writing: {
      title: '包含撰写',
      dataIndex: 'include_document_writing',
      key: 'include_document_writing',
      width: 100,
      render: (_: any, record: Order) => ((record.patent as any)?.include_document_writing ? '是' : '否'),
    },
    patent_type: {
      title: '专利类型',
      dataIndex: 'patent_type',
      key: 'patent_type',
      width: 100,
      render: (_: any, record: Order) => (record.patent as any)?.patent_type || '-',
    },
    appearance_name: {
      title: '外观名称',
      dataIndex: 'appearance_name',
      key: 'appearance_name',
      width: 140,
      render: (_: any, record: Order) => (record.patent as any)?.appearance_name || '-',
    },
    review_type_patent: {
      title: '复审类型',
      dataIndex: 'review_type',
      key: 'review_type_patent',
      width: 120,
      render: (_: any, record: Order) => (record.patent as any)?.review_type || '-',
    },
    rejection_reason: {
      title: '驳回原因',
      dataIndex: 'rejection_reason',
      key: 'rejection_reason',
      width: 180,
      render: (_: any, record: Order) => (record.patent as any)?.rejection_reason || '-',
    },
    postpone_times_patent: {
      title: '复审次数',
      dataIndex: 'postpone_times',
      key: 'postpone_times_patent',
      width: 100,
      render: (_: any, record: Order) => (record.patent as any)?.postpone_times || '-',
    },
    nation: {
      title: '国家/地区',
      dataIndex: 'nation',
      key: 'nation',
      width: 120,
      render: (nation: string, record: Order) => {
        const option = filterOptions.nations.find((opt: FilterOption) => opt.value === nation);
        const label = option?.label || nation || '-';
        // 当为马德里父单时，增加标记
        if (record?.order_type === 'madrid') {
          return (
            <div>
              <span>{label}</span>
              <Tag color="geekblue">马德里基础国</Tag>
            </div>
          );
        }
        return label;
      },
    },
    order_no: {
      title: '订单编号',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 140,
    },
    next_status: {
      title: '下个状态',
      dataIndex: 'next_status',
      key: 'next_status',
      width: 120,
      render: (value: string, record: Order) => record.next_status || value || '-',
    },
    sub_service: {
      title: '子业务名称',
      dataIndex: 'sub_service',
      key: 'sub_service',
      width: 120,
      render: (value: string, record: Order) => {
        const extensionData = record[record.service as keyof Order] as any;
        const raw = (extensionData?.sub_service || value || '').toString();
        if (!raw) return '-';
        const serviceKey = record.service || '';
        const mapping = (SERVICE_TYPE_MAP as Record<string, Record<string, string>>)[serviceKey] || {};
        return mapping[raw] || raw;
      },
    },
    contact_name: {
      title: '客户名称',
      dataIndex: 'contact_name',
      key: 'contact_name',
      width: 140,
      render: (value: string, record: Order) => record.contact_name || value || '-',
    },
     client_email: {
      title: '客户邮箱',
      dataIndex: 'client_email',
      key: 'client_email',
      width: 140,
      render: (value: string, record: Order) => record.client_email || value || '-',
    },
    client_type: {
      title: '客户等级',
      dataIndex: 'client_type',
      key: 'client_type',
      width: 140,
      render: (value: string, record: Order) => record.client_type || value || '-',
    },
    source: {
      title: '客户来源',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (value: string, record: Order) => record.source || value || '-',
    },
    channel: {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (value: string, record: Order) => record.channel || value || '-',
    },
    application_entity_type: {
      title: '申请/权利/持有人主体类型',
      dataIndex: 'application_entity_type',
      key: 'application_entity_type',
      width: 150,
    },
    entity_type: {
      title: '实体类型',
      dataIndex: 'entity_type',
      key: 'entity_type',
      width: 150,
      render: (_: any, record: Order) => (record.patent as any)?.entity_type || '-',
    },
    applicant_name: {
      title: '申请/权利/持有人名称',
      dataIndex: 'applicant_name',
      key: 'applicant_name',
      width: 150,
    },
    applicant_address: {
      title: '申请/权利/持有人地址',
      dataIndex: 'applicant_address',
      key: 'applicant_address',
      width: 150,
    },
    applicant_name_en: {
      title: '申请/权利/持有人英文名称',
      dataIndex: 'applicant_name_en',
      key: 'applicant_name_en',
      width: 150,
    },
    applicant_address_en: {
      title: '申请/权利/持有人英文地址',
      dataIndex: 'applicant_address_en',
      key: 'applicant_address_en',
      width: 150,
    },
    inventor_name: {
      title: '发明人名称',
      dataIndex: 'inventor_name',
      key: 'inventor_name',
      width: 150,
      render: (_: any, record: Order) => (record.patent as any)?.inventor_name || '-',
    },
    inventor_name_en: {
      title: '发明人英文名称',
      dataIndex: 'inventor_name_en',
      key: 'inventor_name_en',
      width: 150,
      render: (_: any, record: Order) => (record.patent as any)?.inventor_name_en || '-',
    },
    inventor_address: {
      title: '发明人地址',
      dataIndex: 'inventor_address',
      key: 'inventor_address',
      width: 150,
      render: (_: any, record: Order) => (record.patent as any)?.inventor_address || '-',
    },
    inventor_address_en: {
      title: '发明人英文地址',
      dataIndex: 'inventor_address_en',
      key: 'inventor_address_en',
      width: 150,
      render: (_: any, record: Order) => (record.patent as any)?.inventor_address_en || '-',
    },
    primary_class: {
      title: '大类',
      dataIndex: 'primary_class',
      key: 'primary_class',
      width: 100,
      render: (value: any, record: Order) => {
        const extensionData = record[record.service as keyof Order] as any;
        const raw = extensionData?.primary_class ?? value;

        // 如果没有值，返回 '-'
        if (!raw) return '-';

        // 如果已经是数组，直接用分号连接
        if (Array.isArray(raw)) {
          return raw.join('；') || '-';
        }

        // 如果是字符串，尝试解析为 JSON 数组
        if (typeof raw === 'string') {
          const trimmed = raw.trim();
          // 尝试解析为 JSON 数组
          if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
              const parsed = JSON.parse(trimmed);
              if (Array.isArray(parsed)) {
                return parsed.join(';') || '-';
              }
            } catch (_) {
              // JSON 解析失败，直接返回原字符串
            }
          }
          // 如果不是 JSON 格式，直接返回原字符串
          return trimmed || '-';
        }

        return '-';
      },
    },
    application_no: {
      title: '申请号',
      dataIndex: 'application_no',
      key: 'application_no',
      width: 140,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (value: string, record: Order) => {
        const extensionData = record[record.service as keyof Order] as any;
        return extensionData?.application_no || value;
      },
    },
    owner_name: {
      title: '申请人/最新权利人',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width: 150,
      render: (value: string, record: Order) => record.applicant_name || value,
    },
    trademark_image: {
      title: '商标图样',
      dataIndex: 'trademark_image',
      key: 'trademark_image',
      width: 140,
      render: (_: any, record: Order) => {
        const ext = record.trademark as any;
        const id = ext?.trademark_image;
        return <TrademarkImageCell mediaId={id} />;
      },
    },
    sub_class: {
      title: '小类',
      dataIndex: 'sub_class',
      key: 'sub_class',
      width: 120,
      render: (value: any, record: Order) => {
        const extensionData = record[record.service as keyof Order] as any;
        const raw = (extensionData?.sub_class ?? value) as any;

        let classList: string[] = [];
        if (Array.isArray(raw)) {
          classList = raw;
        } else if (typeof raw === 'string' && raw) {
          // 尝试解析为 JSON 数组，否则按常见分隔符拆分
          const trimmed = raw.trim();
          if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
              const parsed = JSON.parse(trimmed);
              if (Array.isArray(parsed)) {
                classList = parsed.map(String);
              }
            } catch (_) {
              // ignore json parse error, fallback to split
            }
          }
          if (classList.length === 0) {
            classList = trimmed.split(';').map(s => s.trim());
          }
        }

        classList = (classList || []).filter(Boolean);
        if (classList.length === 0) return '-';

        const tooltipContent = (
          <div style={{ maxWidth: 400 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>小类列表 ({classList.length} 项)：</span>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => {
                  const text = classList.join('\n');
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(text).then(() => {
                      message.success('已复制全部小类');
                    }).catch(() => {
                      message.error('复制失败');
                    });
                  } else {
                    message.error('当前环境不支持自动复制');
                  }
                }}
              >
                复制全部
              </Button>
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <ol style={{ margin: 0, paddingLeft: '20px', listStyleType: 'disc' }}>
                {classList.map((item, index) => (
                  <li key={index} style={{ marginBottom: 8, lineHeight: 1.5 }}>
                    {item}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        );

        return (
          <Popover
            content={tooltipContent}
            placement="topLeft"
            trigger="click"
            overlayStyle={{ maxWidth: 450 }}
          >
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', color: '#1890ff', textDecoration: 'underline' }}
            >
              查看({classList.length})
            </Button>
          </Popover>
        );
      },
    },
    trademark_name: {
      title: '商标名',
      dataIndex: 'trademark_name',
      key: 'trademark_name',
      width: 160,
      render: (_: any, record: Order) => {
        const extensionData = record.trademark as any;
        return extensionData?.trademark_name || '-';
      },
    },
    trademark_status: {
      title: '商标状态',
      dataIndex: 'trademark_status',
      key: 'trademark_status',
      width: 120,
      render: (_: any, record: Order) => {
        const extensionData = record.trademark as any;
        const value = extensionData?.trademark_status;
        return value ? <Tag>{value}</Tag> : '-';
      },
    },

    // ===== 商标扩展字段（record.trademark）部分（节选，保持与现有一致） =====
    registration_no: {
      title: '注册号',
      dataIndex: 'registration_no',
      key: 'registration_no',
      width: 140,
      render: (value: string, record: Order) => {
        const extensionData = record[record.service as keyof Order] as any;
        return extensionData?.registration_no || value;
      },
    },
    sale_status: {
      title: '出售状态',
      dataIndex: 'sale_status',
      key: 'sale_status',
      width: 140,
      render: (_: any, record: Order) => {
        const value = (record.trademark as any)?.sale_status;
        if (!value) return '-';
        const color = saleStatusTagColorMap[value] || 'blue';
        return <Tag color={color}>{value}</Tag>;
      },
    },
    sold_at: {
      title: '出售时间',
      dataIndex: 'sold_at',
      key: 'sold_at',
      width: 160,
      render: (_: any, record: Order) => {
        const v = (record.trademark as any)?.sold_at;
        return v ? dayjs(v).format('YYYY-MM-DD') : '-';
      },
    },
    trademark_type: {
      title: '商标类型',
      dataIndex: 'trademark_type',
      key: 'trademark_type',
      width: 120,
      render: (_: any, record: Order) => (record.trademark as any)?.trademark_type || '-',
    },
    application_basics: {
      title: '申请基础',
      dataIndex: 'application_basics',
      key: 'application_basics',
      width: 140,
      render: (_: any, record: Order) => (record.trademark as any)?.application_basics || '-',
    },
    a1_first_use_at: {
      title: '首次使用日期',
      dataIndex: 'a1_first_use_at',
      key: 'a1_first_use_at',
      width: 140,
      render: (_: any, record: Order) => {
        const v = (record.trademark as any)?.a1_first_use_at;
        return v ? dayjs(v).format('YYYY-MM-DD') : '-';
      },
    },
    a1_first_commercial_use_at: {
      title: '首次商用日期',
      dataIndex: 'a1_first_commercial_use_at',
      key: 'a1_first_commercial_use_at',
      width: 160,
      render: (_: any, record: Order) => {
        const v = (record.trademark as any)?.a1_first_commercial_use_at;
        return v ? dayjs(v).format('YYYY-MM-DD') : '-';
      },
    },
    b44_registration_no: {
      title: '44(e)注册号',
      dataIndex: 'b44_registration_no',
      key: 'b44_registration_no',
      width: 140,
      render: (_: any, record: Order) => (record.trademark as any)?.b44_registration_no || '-',
    },
    b44_registration_at: {
      title: '44(e)注册日期',
      dataIndex: 'b44_registration_at',
      key: 'b44_registration_at',
      width: 150,
      render: (_: any, record: Order) => {
        const v = (record.trademark as any)?.b44_registration_at;
        return v ? dayjs(v).format('YYYY-MM-DD') : '-';
      },
    },
    d44_application_no: {
      title: '44(d)申请号',
      dataIndex: 'd44_application_no',
      key: 'd44_application_no',
      width: 140,
      render: (_: any, record: Order) => (record.trademark as any)?.d44_application_no || '-',
    },
    d44_application_at: {
      title: '44(d)申请日期',
      dataIndex: 'd44_application_at',
      key: 'd44_application_at',
      width: 150,
      render: (_: any, record: Order) => {
        const v = (record.trademark as any)?.d44_application_at;
        return v ? dayjs(v).format('YYYY-MM-DD') : '-';
      },
    },
    priority: {
      title: '优先权',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (_: any, record: Order) => {
        const v = (record.trademark as any)?.priority || (record.patent as any)?.priority;
        if (typeof v === 'boolean') return v ? '有优先权' : '无优先权';
        return v || '-';
      },
    },
    priority_no: {
      title: '优先权号',
      dataIndex: 'priority_no',
      key: 'priority_no',
      width: 140,
      render: (_: any, record: Order) => (record.trademark as any)?.priority_no || (record.patent as any)?.priority_no || '-',
    },
    priority_date: {
      title: '优先权日期',
      dataIndex: 'priority_date',
      key: 'priority_date',
      width: 140,
      render: (_: any, record: Order) => {
        const v = (record.trademark as any)?.priority_date || (record.patent as any)?.priority_date;
        return v ? dayjs(v).format('YYYY-MM-DD') : '-';
      },
    },
    base_country_trademark_name: {
      title: '基础国商标名称',
      dataIndex: 'base_country_trademark_name',
      key: 'base_country_trademark_name',
      width: 180,
      render: (_: any, record: Order) => (record.trademark as any)?.base_country_trademark_name || '-',
    },
    base_country_application_no: {
      title: '基础国申请号',
      dataIndex: 'base_country_application_no',
      key: 'base_country_application_no',
      width: 160,
      render: (_: any, record: Order) => (record.trademark as any)?.base_country_application_no || '-',
    },
    base_country_registration_no: {
      title: '基础国注册号',
      dataIndex: 'base_country_registration_no',
      key: 'base_country_registration_no',
      width: 160,
      render: (_: any, record: Order) => (record.trademark as any)?.base_country_registration_no || '-',
    },
    international_registration_no: {
      title: '国际注册号',
      dataIndex: 'international_registration_no',
      key: 'international_registration_no',
      width: 160,
      render: (_: any, record: Order) => (record.trademark as any)?.international_registration_no || '-',
    },
    trademark_meaning: {
      title: '商标含义',
      dataIndex: 'trademark_meaning',
      key: 'trademark_meaning',
      width: 180,
      render: (_: any, record: Order) => (record.trademark as any)?.trademark_meaning || '-',
    },
    color_statement: {
      title: '颜色声明',
      dataIndex: 'color_statement',
      key: 'color_statement',
      width: 160,
      render: (_: any, record: Order) => (record.trademark as any)?.color_statement || '-',
    },
    assignee_entity_type: {
      title: '受让人主体类型',
      dataIndex: 'assignee_entity_type',
      key: 'assignee_entity_type',
      width: 140,
      render: (_: any, record: Order) => (record.trademark as any)?.assignee_entity_type || '-',
    },
    new_applicant_name: {
      title: '变更后名称',
      dataIndex: 'new_applicant_name',
      key: 'new_applicant_name',
      width: 160,
      render: (_: any, record: Order) => (record.trademark as any)?.new_applicant_name || '-',
    },
    new_applicant_name_en: {
      title: '变更后英文名',
      dataIndex: 'new_applicant_name_en',
      key: 'new_applicant_name_en',
      width: 180,
      render: (_: any, record: Order) => (record.trademark as any)?.new_applicant_name_en || '-',
    },
    new_applicant_address: {
      title: '变更后地址',
      dataIndex: 'new_applicant_address',
      key: 'new_applicant_address',
      width: 160,
      render: (_: any, record: Order) => (record.trademark as any)?.new_applicant_address || '-',
    },
    new_applicant_address_en: {
      title: '变更后英文地址',
      dataIndex: 'new_applicant_address_en',
      key: 'new_applicant_address_en',
      width: 180,
      render: (_: any, record: Order) => (record.trademark as any)?.new_applicant_address_en || '-',
    },
    include_transfer_service: {
      title: '包含转让服务',
      dataIndex: 'include_transfer_service',
      key: 'include_transfer_service',
      width: 140,
      render: (_: any, record: Order) => (record.trademark as any)?.include_transfer_service || '-',
    },
    assignment_type: {
      title: '转让类型',
      dataIndex: 'assignment_type',
      key: 'assignment_type',
      width: 120,
      render: (_: any, record: Order) => (record.trademark as any)?.assignment_type || '-',
    },
    postpone_type: {
      title: '延期类型',
      dataIndex: 'postpone_type',
      key: 'postpone_type',
      width: 140,
      render: (_: any, record: Order) => (record.trademark as any)?.postpone_type || '-',
    },
    postpone_times: {
      title: '延期次数',
      dataIndex: 'postpone_times',
      key: 'postpone_times',
      width: 100,
      render: (_: any, record: Order) => (record.trademark as any)?.postpone_times || '-',
    },
    postpone_reason: {
      title: '延期原因',
      dataIndex: 'postpone_reason',
      key: 'postpone_reason',
      width: 180,
      render: (_: any, record: Order) => (record.trademark as any)?.postpone_reason || '-',
    },
    review_type: {
      title: '复审类型',
      dataIndex: 'review_type',
      key: 'review_type',
      width: 120,
      render: (_: any, record: Order) => (record.trademark as any)?.review_type || '-',
    },
    review_times: {
      title: '复审次数',
      dataIndex: 'review_times',
      key: 'review_times',
      width: 100,
      render: (_: any, record: Order) => (record.trademark as any)?.review_times || '-',
    },
    rationale_basis: {
      title: '理由基础',
      dataIndex: 'rationale_basis',
      key: 'rationale_basis',
      width: 180,
      render: (_: any, record: Order) => (record.trademark as any)?.rationale_basis || '-',
    },
    revocation_type: {
      title: '撤销类型',
      dataIndex: 'revocation_type',
      key: 'revocation_type',
      width: 120,
      render: (_: any, record: Order) => (record.trademark as any)?.revocation_type || '-',
    },
    search_method: {
      title: '检索方式',
      dataIndex: 'search_method',
      key: 'search_method',
      width: 120,
      render: (_: any, record: Order) => (record.trademark as any)?.search_method || '-',
    },
    oath_type: {
      title: '宣誓类型',
      dataIndex: 'oath_type',
      key: 'oath_type',
      width: 120,
      render: (_: any, record: Order) => (record.trademark as any)?.oath_type || '-',
    },
    renewal_period: {
      title: '续展期次',
      dataIndex: 'renewal_period',
      key: 'renewal_period',
      width: 120,
      render: (_: any, record: Order) => (record.trademark as any)?.renewal_period || (record.barcode as any)?.renewal_period || '-',
    },
    original_agent: {
      title: '原代理人',
      dataIndex: 'original_agent',
      key: 'original_agent',
      width: 140,
      render: (_: any, record: Order) => (record.trademark as any)?.original_agent || '-',
    },
    cancellation_reason: {
      title: '注销原因',
      dataIndex: 'cancellation_reason',
      key: 'cancellation_reason',
      width: 180,
      render: (_: any, record: Order) => (record.trademark as any)?.cancellation_reason || (record.barcode as any)?.cancellation_reason || (record.company as any)?.cancellation_reason || '-',
    },
    certificate_type: {
      title: '证书类型',
      dataIndex: 'certificate_type',
      key: 'certificate_type',
      width: 120,
      render: (_: any, record: Order) => (record.trademark as any)?.certificate_type || '-',
    },
    change_items: {
      title: '变更项目',
      dataIndex: 'change_items',
      key: 'change_items',
      width: 200,
      render: (_: any, record: Order) => {
        const v = (record.trademark as any)?.change_items || (record.company as any)?.change_items;
        if (Array.isArray(v)) return v.join('，');
        return v || '-';
      },
    },
    inventor: {
      title: '发明人',
      dataIndex: 'inventor',
      key: 'inventor',
      width: 120,
    },
    copyright_type: {
      title: '作品类型',
      dataIndex: 'copyright_type',
      key: 'copyright_type',
      width: 120,
    },

    // ===== 公司扩展字段（record.company） =====
    company_name: {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 150,
      render: (_: any, record: Order) => (record.company as any)?.company_name || (record.barcode as any)?.company_name || '-',
    },
    company_no: {
      title: '公司编号',
      dataIndex: 'company_no',
      key: 'company_no',
      width: 140,
      render: (_: any, record: Order) => (record.company as any)?.company_no || '-',
    },
    company_address: {
      title: '公司地址',
      dataIndex: 'company_address',
      key: 'company_address',
      width: 220,
      render: (_: any, record: Order) => (record.company as any)?.company_address || '-',
    },
    company_type: {
      title: '公司类型',
      dataIndex: 'company_type',
      key: 'company_type',
      width: 140,
      render: (_: any, record: Order) => (record.company as any)?.company_type || '-',
    },
    legal_representative: {
      title: '法人代表',
      dataIndex: 'legal_representative',
      key: 'legal_representative',
      width: 140,
      render: (_: any, record: Order) => (record.company as any)?.legal_representative || '-',
    },
    include_vat_no: {
      title: '含税号',
      dataIndex: 'include_vat_no',
      key: 'include_vat_no',
      width: 100,
      render: (_: any, record: Order) => ((record.company as any)?.include_vat_no ? '是' : '否'),
    },
    include_bank_account: {
      title: '含开户',
      dataIndex: 'include_bank_account',
      key: 'include_bank_account',
      width: 100,
      render: (_: any, record: Order) => ((record.company as any)?.include_bank_account ? '是' : '否'),
    },
    include_foreign_directors: {
      title: '需要外籍董事',
      dataIndex: 'include_foreign_directors',
      key: 'include_foreign_directors',
      width: 120, render: (_: any, record: Order) => ((record.company as any)?.include_foreign_directors ? '是' : '否'),
    },
    annual_inspection_times: {
      title: '年审次数',
      dataIndex: 'annual_inspection_times',
      key: 'annual_inspection_times',
      width: 120,
      render: (_: any, record: Order) => (record.company as any)?.annual_inspection_times || '-',
    },
    account_bank: {
      title: '开户银行',
      dataIndex: 'account_bank',
      key: 'account_bank',
      width: 140,
      render: (_: any, record: Order) => (record.company as any)?.account_bank || '-',
    },

    // ===== 条形码扩展字段（record.barcode） =====
    company_name_barcode: {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name_barcode',
      width: 150,
      render: (_: any, record: Order) => (record.barcode as any)?.company_name || '-',
    },
    quantity: {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity_barcode',
      width: 100,
      render: (_: any, record: Order) => (record.barcode as any)?.quantity ?? '-',
    },
    validity_period: {
      title: '有效期限',
      dataIndex: 'validity_period',
      key: 'validity_period',
      width: 140,
      render: (_: any, record: Order) => (record.barcode as any)?.validity_period || '-',
    },
    renewal_period_barcode: {
      title: '续费周期',
      dataIndex: 'renewal_period',
      key: 'renewal_period_barcode',
      width: 120,
      render: (_: any, record: Order) => (record.barcode as any)?.renewal_period ?? '-',
    },
    quantity_original: {
      title: '原始数量',
      dataIndex: 'quantity_original',
      key: 'quantity_original',
      width: 120,
      render: (_: any, record: Order) => (record.barcode as any)?.quantity_original ?? '-',
    },
    quantity_increase: {
      title: '本次增加数量',
      dataIndex: 'quantity_increase',
      key: 'quantity_increase',
      width: 140,
      render: (_: any, record: Order) => (record.barcode as any)?.quantity_increase ?? '-',
    },

    service_name: {
      title: '业务名',
      dataIndex: 'service_name',
      key: 'service_name',
      width: 140,
      render: (_: any, record: Order) => (record.other as any)?.service_name ?? '-',
    },

    receipt_at: {
      title: '回执日期',
      dataIndex: 'receipt_at',
      key: 'receipt_at',
      width: 120,
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },

    announcement_at: {
      title: '公告日期',
      dataIndex: 'announcement_at',
      key: 'announcement_at',
      width: 120,
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },

    decline_at: {
      title: '驳回日期',
      dataIndex: 'decline_at',
      key: 'decline_at',
      width: 120,
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },

    approve_at: {
      title: '注册日期',
      dataIndex: 'approve_at',
      key: 'approve_at',
      width: 120,
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },

    payment_status: {
      title: '付款状态',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },

    payment_at: {
      title: '付款日期',
      dataIndex: 'payment_at',
      key: 'payment_at',
      width: 120,
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    due_at: {
      title: '截止日期',
      dataIndex: 'due_at',
      key: 'due_at',
      width: 120,
      render: (date: string) => {
        if (!date) return '-';
        const d = dayjs(date);
        const days = d.startOf('day').diff(dayjs().startOf('day'), 'day');
        const text = days < 0 ? '已过期' : `剩余${days}天`;
        const color = days < 0 ? '#ff4d4f' : '#888';
        return (
          <div>
            <div>{d.format('YYYY-MM-DD')}</div>
            <div style={{ color, fontSize: 12 }}>{text}</div>
          </div>
        );
      },
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    expiration_at: {
      title: '异常订单',
      dataIndex: 'expiration_at',
      key: 'expiration_at',
      width: 120,
      render: (date: string) => {
        if (!date) return '-';
        const d = dayjs(date);
        const days = d.startOf('day').diff(dayjs().startOf('day'), 'day');
        const text = days < 0 ? '已过期' : `剩余${days}天`;
        const color = days < 0 ? '#ff4d4f' : '#888';
        return (
          <div>
            <div>{d.format('YYYY-MM-DD')}</div>
            <div style={{ color, fontSize: 12 }}>{text}</div>
          </div>
        );
      },
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    created_at: {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
    },
    service: {
      title: '业务类型',
      dataIndex: 'service',
      key: 'service',
      width: 100,
      render: (service: string) => {
        const serviceTypeInfo = SERVICE_TYPES.find((s) => s.value === service);
        return (
          <Tag color="blue">
            {serviceTypeInfo?.icon} {serviceTypeInfo?.label?.replace('服务', '') || service}
          </Tag>
        );
      },
    },
    price: {
      title: '成交价格',
      dataIndex: 'price',
      key: 'price',
      width: 110,
      render: (price: string) => (price ? `¥${price}` : '-'),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    cost: {
      title: '成本',
      dataIndex: 'cost',
      key: 'cost',
      width: 100,
      render: (cost: string) => (cost ? `¥${cost}` : '-'),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    profit: {
      title: '利润',
      dataIndex: 'profit',
      key: 'profit',
      width: 100,
      render: (profit: string) => (profit ? `¥${profit}` : '-'),
    },
    seller: {
      title: '所属销售',
      dataIndex: 'seller',
      key: 'seller',
      width: 100,
      render: (_: any, record: Order) => {
        const sales: any = (record as any).sales;
        const byAccessor = sales?.contact_name;
        const byDisplay = sales?.display_name || sales?.username || sales?.email;
        const bySellerName = (record as any).seller_name;
        return byAccessor || byDisplay || bySellerName || (record as any).seller || '-';
      },
    },
    actions: {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 220,
      render: (_: any, record: Order) => (
        <div className="orders-actions">
          <Space direction="vertical" size={2}>
            <Space size="small">
              {canEdit && <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small">编辑</Button>}
              {canViewDetail && <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record.id)} size="small">查看</Button>}
              {canUpdateStatus && <Button type="link" icon={<ScheduleOutlined />} onClick={() => openUpdateStatusModal(record)} size="small">更新状态</Button>}
            </Space>
            <Space size="small">
              {canCopy && <Button type="link" icon={<CopyOutlined />} onClick={() => handleCopy(record)} size="small">复制</Button>}
              {canDelete && (
                <Popconfirm title="确定删除这个订单吗？" onConfirm={() => record.id && handleDelete(record.id)} okText="确定" cancelText="取消">
                  <Button type="link" danger icon={<DeleteOutlined />} size="small">删除</Button>
                </Popconfirm>
              )}
              {canMonitor && serviceType === 'patent' && record.id && (
                <Button
                  size="small"
                  type="link"
                  icon={<DollarOutlined />}
                  onClick={() => {
                    const orderId = record.id as number;
                    window.location.href = `/patent-payments?order_id=${orderId}&openCreate=1`;
                  }}
                >
                  监控
                </Button>
              )}
              {canMonitor && serviceType === 'trademark' && monitoredNationValues.has(record.nation || '') && (() => {
                const extensionData = record.trademark as any;
                const applicationNo = extensionData?.application_no;
                if (!applicationNo) return null;
                if ((record as any).is_monitored) {
                  return <Tag color="green">已监控</Tag>;
                }
                return (
                  <Button size="small" type="link" icon={<PlusCircleOutlined />} onClick={() => handleAddToMonitor(record, applicationNo)}>
                    + 监控
                  </Button>
                );
              })()}
            </Space>
          </Space>
        </div>
      ),
    },
  };

  return allColumnDefinitions;
}
